import { cn } from '~/utils/cn';
import { formatDateShort } from '~/utils/contractLabels';
import { formatCentsToBRLCompact } from '~/utils/formatFinance';
import type {
  AttentionItem,
  AttentionUrgency,
} from '~/store/contracts/selectors';

import BankAvatar from '../../atoms/BankAvatar/view';
import { Icon } from '../../atoms/Icon/view';
import { Text } from '../../atoms/Text/view';

interface PropsAttentionRow {
  item: AttentionItem;
  onClick?: () => void;
  className?: string;
}

const URGENCY_STYLE: Record<
  AttentionUrgency,
  { dot: string; pillBg: string; pillText: string; amount: string; label: (d: number) => string }
> = {
  overdue: {
    dot: 'bg-warning-04',
    pillBg: 'bg-warning-01',
    pillText: 'color-warning-06',
    amount: 'color-warning-06',
    label: (d) => (d === -1 ? 'Vencida ontem' : `Vencida há ${Math.abs(d)}d`),
  },
  today: {
    dot: 'bg-alert-04',
    pillBg: 'bg-alert-01',
    pillText: 'color-alert-06',
    amount: 'color-complement-01',
    label: () => 'Vence hoje',
  },
  soon: {
    dot: 'bg-alert-03',
    pillBg: 'bg-alert-01',
    pillText: 'color-alert-06',
    amount: 'color-complement-01',
    label: (d) => (d === 1 ? 'Vence amanhã' : `Vence em ${d}d`),
  },
  upcoming: {
    dot: 'bg-intense-04',
    pillBg: 'bg-intense-03',
    pillText: 'color-intense-06',
    amount: 'color-complement-01',
    label: (d) => `Vence em ${d}d`,
  },
};

export default function AttentionRow({ item, onClick, className }: PropsAttentionRow) {
  const { installment, contract, urgency, daysDiff } = item;
  const style = URGENCY_STYLE[urgency];
  const total = contract?.installmentsTotal;

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group flex w-full items-center gap-small rounded-small bg-intense-01 border border-intense-03 px-small py-smaller text-left hover:bg-intense-02 transition-colors',
        className,
      )}
    >
      <div className="relative shrink-0">
        <BankAvatar institution={contract?.institution} size="md" />
        <span
          className={cn(
            'absolute -bottom-[2px] -right-[2px] h-[12px] w-[12px] rounded-full border-2 border-intense-01',
            style.dot,
          )}
        />
      </div>
      <div className="flex-1 min-w-0">
        <Text className="text-bodyLG color-complement-01 truncate" numberOfLines={1}>
          {contract?.institution.name ?? 'Contrato'}
        </Text>
        <div className="mt-quark flex items-center gap-quark">
          <Icon
            name="icon-calendar"
            size={13}
            color="#606060"
            rounded={false}
            className="shrink-0"
          />
          <Text className="text-bodyMD color-complement-01" numberOfLines={1}>
            {`Vence ${formatDateShort(installment.dueDate)}`}
          </Text>
        </div>
        <Text className="mt-quark text-captionMD color-intense-06" numberOfLines={1}>
          {`Parcela ${installment.index}${total ? `/${total}` : ''}`}
        </Text>
      </div>
      <div className="flex flex-col items-end gap-quark shrink-0">
        <Text className={cn('text-paragraphMD', style.amount)}>
          {formatCentsToBRLCompact(installment.amount)}
        </Text>
        <span
          className={cn(
            'inline-flex items-center px-smaller py-[2px] rounded-full',
            style.pillBg,
          )}
        >
          <Text className={cn('text-caption', style.pillText)}>
            {style.label(daysDiff)}
          </Text>
        </span>
      </div>
      <Icon
        name="icon-chevron-right"
        size={14}
        color="#B0B0B0"
        rounded={false}
        className="shrink-0"
      />
    </button>
  );
}
